import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShop } from '../../../shared/store';

// 1. Types
type HeroBook = {
  id: number;
  title: string;
  author: string;
  spine: string;
  cover: string;
  imageUrl?: string;
};

type Book3DProps = {
  book: HeroBook;
  offset: number;
  onActivate: () => void;
};

// 2. Helper Component: Book3D
const Book3D = ({ book, offset, onActivate }: Book3DProps) => {
  const displayImage = book.imageUrl;
  const [hovering, setHovering] = useState(false);

  const isActive = offset === 0;
  const absOffset = Math.abs(offset);

  const bookWidth = isActive ? 140 : absOffset === 1 ? 115 : absOffset === 2 ? 95 : 80;
  const bookHeight = isActive ? 210 : absOffset === 1 ? 172 : absOffset === 2 ? 142 : 120;
  const spineWidth = isActive ? 20 : absOffset === 1 ? 16 : absOffset === 2 ? 13 : 11;

  const translateY = isActive ? 0 : absOffset === 1 ? 20 : absOffset === 2 ? 38 : 50;
  const opacity = isActive ? 1 : absOffset === 1 ? 0.85 : absOffset === 2 ? 0.65 : 0.45;
  const zIndex = isActive ? 30 : absOffset === 1 ? 20 : absOffset === 2 ? 15 : 10 - absOffset;
  const rotateY = offset < 0 ? 12 : offset > 0 ? -12 : 0;

  return (
    <div
      onClick={() => {
        if (!isActive) onActivate();
      }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        display: 'flex',
        flexShrink: 0,
        cursor: isActive ? 'default' : 'pointer',
        transform: `translateY(${translateY}px) perspective(600px) rotateY(${rotateY}deg)`,
        opacity,
        zIndex,
        transition: 'all 0.5s cubic-bezier(0.34, 1.4, 0.64, 1)',
        transformOrigin: offset < 0 ? 'right center' : offset > 0 ? 'left center' : 'center',
        filter: isActive ? 'none' : `brightness(${absOffset === 1 ? 0.8 : 0.55})`,
      }}
    >
      {/* Spine */}
      <div
        style={{
          width: `${spineWidth}px`,
          height: `${bookHeight}px`,
          borderRadius: '3px 0 0 3px',
          background: `linear-gradient(180deg, ${book.spine}ff 0%, ${book.spine}cc 30%, ${book.spine}99 70%, ${book.spine}bb 100%)`,
          boxShadow: 'inset -3px 0 6px rgba(0,0,0,0.4), inset 2px 0 4px rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <p style={{
            transform: 'rotate(-90deg)',
            whiteSpace: 'nowrap',
            fontSize: isActive ? '9px' : '7px',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.6)',
            letterSpacing: '0.05em',
            margin: 0,
            maxWidth: `${bookHeight - 16}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {book.title}
        </p>
      </div>

      {/* Cover */}
      <div
        style={{
          width: `${bookWidth}px`,
          height: `${bookHeight}px`,
          borderRadius: '0 6px 6px 0',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: isActive
            ? '6px 12px 40px rgba(0,0,0,0.6), 3px 0 0 rgba(0,0,0,0.3)'
            : '4px 8px 24px rgba(0,0,0,0.45)',
          flexShrink: 0,
        }}
      >
        <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: displayImage ? `url(${displayImage})` : `linear-gradient(170deg, ${book.cover}ff 0%, ${book.cover}99 50%, ${book.spine}ff 100%)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: 'transform 0.4s ease',
            transform: isActive && hovering ? 'scale(1.04)' : 'scale(1)',
          }}
        />

        {/* Text Overlay */}
        <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 3,
            background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 50%, transparent 100%)',
            padding: isActive ? '18px 14px 14px' : '12px 10px 10px',
            transition: 'opacity 0.2s',
          }}>
          <p style={{
              fontSize: isActive ? '13px' : '10px',
              fontWeight: 700,
              color: '#fff',
              lineHeight: 1.2,
              margin: 0,
              marginBottom: '3px',
              textShadow: '0 1px 6px rgba(0,0,0,0.9)',
            }}>
            {book.title}
          </p>
          <p style={{
              fontFamily: 'system-ui',
              fontSize: isActive ? '9px' : '8px',
              color: '#ff9a6c',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontWeight: 600,
              margin: 0,
            }}>
            {book.author}
          </p>
        </div>

      </div>
    </div>
  );
};

// 3. Main Component
const BooksHero = () => {
  const { t } = useTranslation();
  const { products, themeMode } = useShop();
  const [activeIndex, setActiveIndex] = useState(0);
  const isDark = themeMode === 'dark';

  const BOOKS: HeroBook[] = products.length > 0 
    ? products.slice(0, 6).map((product, index) => ({
        id: index + 1,
        title: t(product.nameKey || 'Untitled'),
        author: t(product.categoryKey || 'Unknown Author'),
        spine: '#1a3a6b',
        cover: '#1e4a8a',
        imageUrl: product.imageUrl,
      }))
    : [
        { id: 1, title: 'Beauty & the Beast', author: 'Disney Classics', spine: '#1a3a6b', cover: '#1e4a8a' },
        { id: 2, title: 'Fire and Blood', author: 'George R.R. Martin', spine: '#6b1a1a', cover: '#8a1e1e' },
        { id: 3, title: 'Chronicles of Narnia', author: 'C.S. Lewis', spine: '#1a4a3a', cover: '#1e6b4a' },
        { id: 4, title: 'The Great Gatsby', author: 'F. Scott Fitzgerald', spine: '#4a1a6b', cover: '#6a1e8a' },
        { id: 5, title: 'To Kill a Mockingbird', author: 'Harper Lee', spine: '#6b4a1a', cover: '#8a6a1e' },
        { id: 6, title: '1984', author: 'George Orwell', spine: '#1a6b4a', cover: '#1e8a6a' },
      ];

  const go = (direction: -1 | 1) => {
    setActiveIndex((prev) => (prev + direction + BOOKS.length) % BOOKS.length);
  };

  const getOffset = (index: number) => {
    let offset = index - activeIndex;
    if (offset > BOOKS.length / 2) offset -= BOOKS.length;
    if (offset < -BOOKS.length / 2) offset += BOOKS.length;
    return offset;
  };

  const visibleBooks = BOOKS.map((book, index) => ({ 
    ...book, 
    index, 
    offset: getOffset(index) 
  }))
    .filter((book) => Math.abs(book.offset) <= 3)
    .sort((a, b) => Math.abs(b.offset) - Math.abs(a.offset));

  const activeBook = BOOKS[activeIndex] || BOOKS[0];

  if (!BOOKS.length) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400&display=swap');
        @keyframes bookhero-slide-up { from { opacity:0; transform:translateY(28px); } to { opacity:1; transform:translateY(0); } }
        .bookhero-su { animation: bookhero-slide-up 0.7s cubic-bezier(0.16,1,0.3,1) both; }
        .bookhero-s1{animation-delay:0.05s} .bookhero-s2{animation-delay:0.15s} .bookhero-s3{animation-delay:0.27s} 
        .bookhero-cta-btn:hover { transform: translateY(-3px) !important; box-shadow: 0 12px 32px rgba(255,107,43,0.5) !important; }
        .bookhero-shell { position: relative; width: 100vw; left: 50%; right: 50%; margin-left: -50vw; margin-right: -50vw; min-height: calc(100svh - 86px); background: transparent; }
        .bookhero-container { width: 100%; max-width: 80rem; margin: 0 auto; padding: 0 1rem; box-sizing: border-box; }
        @media (min-width: 640px) { .bookhero-container { padding-left: 1.5rem; padding-right: 1.5rem; } }
        @media (min-width: 1024px) { .bookhero-container { padding-left: 2rem; padding-right: 2rem; } }
        .bookhero-stage { width: 100%; display: grid; grid-template-columns: 1fr 1fr; min-height: calc(100svh - 86px); }
        @media (max-width: 1024px) { .bookhero-stage { grid-template-columns: 1fr; } }
      `}</style>

      <div
        className="bookhero-shell"
        style={{
          background: isDark ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(135deg, #fff7ed 0%, #ffffff 48%, #fff1e8 100%)',
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'stretch',
          padding: 0,
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <div className="bookhero-container">
        <div
          className="bookhero-stage"
          style={{
            width: '100%',
            maxWidth: '80rem',
            margin: '0 auto',
            overflow: 'hidden',
            background: isDark ? '#162033' : 'transparent',
            boxShadow: isDark ? '0 40px 100px rgba(0,0,0,0.5), 0 16px 48px rgba(0,0,0,0.3)' : 'none',
            border: isDark ? '1px solid rgba(255,107,43,0.2)' : 'none',
            borderRadius: isDark ? '24px' : '0px',
          }}
        >
          <div
            className="bookhero-left"
            style={{
              background: isDark ? '#1e293b' : 'transparent',
              padding: '56px 52px',
              display: 'flex',
              flexDirection: 'column',
              gap: '0',
              position: 'relative',
            }}
          >
            <div className="bookhero-su bookhero-s1" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '32px' }}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #ff6b2b, #ff4000)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 14px rgba(255,107,43,0.4)',
                }}
              >
                <svg width="17" height="17" fill="white" viewBox="0 0 24 24">
                  <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span style={{ fontSize: '11px', letterSpacing: '0.14em', color: '#ff6b2b', fontWeight: 700, textTransform: 'uppercase' }}>
                Reading Library
              </span>
            </div>

            <h1
              className="bookhero-su bookhero-s2"
              style={{
                fontSize: '52px',
                fontWeight: 700,
                lineHeight: 1.1,
                color: isDark ? '#f1f5f9' : '#0c0500',
                letterSpacing: '-0.5px',
                marginBottom: '18px',
              }}
            >
              Keep the
              <br />
              <span style={{ color: '#ff6b2b' }}>story going..</span>
            </h1>

            <p
              className="bookhero-su bookhero-s3"
              style={{
                fontSize: '14px',
                color: isDark ? '#94a3b8' : '#6b7280',
                lineHeight: '1.75',
                maxWidth: '300px',
                marginBottom: '34px',
              }}
            >
              Don&apos;t let the story end just yet. Continue reading your last book and immerse yourself in the world of literature.
            </p>

            <div className="bookhero-su bookhero-s3" style={{ display: 'flex', gap: '12px', marginBottom: 'auto' }}>
              <button
                className="bookhero-cta-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  borderRadius: '50px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #ff6b2b, #ff3d00)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 6px 22px rgba(255,107,43,0.4)',
                }}
              >
                Start reading
                <svg width="13" height="13" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M17 7H7M17 7v10" />
                </svg>
              </button>
              <button
                className="bookhero-ghost-btn"
                style={{
                  padding: '14px 22px',
                  borderRadius: '50px',
                  border: isDark ? '1.5px solid rgba(255,107,43,0.3)' : '1.5px solid #e8e0d8',
                  background: 'transparent',
                  color: isDark ? '#94a3b8' : '#6b7280',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Browse all
              </button>
            </div>

            <div className="bookhero-su bookhero-s4" style={{ marginTop: '36px' }}>
              <div
                style={{
                  background: isDark ? 'rgba(255,107,43,0.1)' : '#fff9f6',
                  borderRadius: '16px',
                  borderLeft: '3px solid #ff6b2b',
                  padding: '18px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                }}
              >
                <div
                  style={{
                    width: '38px',
                    height: '54px',
                    borderRadius: '5px',
                    flexShrink: 0,
                    background: activeBook.imageUrl
                      ? `url(${activeBook.imageUrl}) center/cover`
                      : `linear-gradient(160deg, ${activeBook.cover} 0%, ${activeBook.spine} 100%)`,
                    boxShadow: '0 3px 10px rgba(0,0,0,0.25)',
                  }}
                />
                <div>
                  <p
                    style={{
                      fontSize: '9px',
                      color: '#ff6b2b',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      marginBottom: '4px',
                    }}
                  >
                    Now Featured
                  </p>
                  <p
                    style={{
                      fontSize: '15px',
                      fontWeight: 700,
                      color: isDark ? '#f1f5f9' : '#1a0800',
                      marginBottom: '2px',
                    }}
                  >
                    {activeBook.title}
                  </p>
                  <p style={{ fontSize: '11px', color: isDark ? '#94a3b8' : '#6b7280' }}>{activeBook.author}</p>
                </div>
              </div>
            </div>

            <div className="bookhero-su bookhero-s5" style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '11px', color: isDark ? '#94a3b8' : '#6b7280' }}>Reading progress</span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#ff6b2b' }}>
                  04 <span style={{ color: '#9ca3af', fontWeight: 400 }}>/ 60 books</span>
                </span>
              </div>
              <div style={{ height: '4px', background: isDark ? 'rgba(255,255,255,0.1)' : '#f0e8e0', borderRadius: '99px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: '7%',
                    background: 'linear-gradient(to right, #ff6b2b, #ff9060)',
                    borderRadius: '99px',
                    boxShadow: '0 0 8px rgba(255,107,43,0.5)',
                  }}
                />
              </div>
            </div>
          </div>

          <div
            className="bookhero-right"
            style={{
              background: isDark ? '#1e293b' : 'transparent',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 24px 36px',
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '30%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '320px',
                height: '320px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,107,43,0.16) 0%, transparent 68%)',
                pointerEvents: 'none',
              }}
            />

            <p
              style={{
                fontSize: '9px',
                letterSpacing: '0.35em',
                color: isDark ? 'rgba(255,107,43,0.7)' : 'rgba(194,65,12,0.58)',
                textTransform: 'uppercase',
                marginBottom: '28px',
                zIndex: 5,
              }}
            >
              ✦ Your Collection ✦
            </p>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                gap: '8px',
                height: '280px',
                width: '100%',
                zIndex: 5,
                perspective: '800px',
              }}
            >
              {visibleBooks.map((book) => (
                <Book3D
                  key={book.id}
                  book={book}
                  offset={book.offset}
                  onActivate={() => setActiveIndex(book.index)}
                />
              ))}
            </div>

            <div
              style={{
                width: '240px',
                height: '16px',
                background: 'radial-gradient(ellipse, rgba(255,107,43,0.2) 0%, transparent 70%)',
                filter: 'blur(10px)',
                marginTop: '4px',
                zIndex: 4,
              }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '28px', zIndex: 5 }}>
              <button
                className="bookhero-nav-btn"
                onClick={() => go(-1)}
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  border: isDark ? '1px solid rgba(255,107,43,0.3)' : '1px solid rgba(249,115,22,0.28)',
                  background: isDark ? '#0f172a' : '#fff',
                  color: isDark ? '#ff6b2b' : '#9a3412',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(8px)',
                  boxShadow: isDark ? '0 6px 18px rgba(255,107,43,0.3)' : '0 6px 18px rgba(249,115,22,0.14)',
                }}
              >
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {BOOKS.map((book, index) => (
                <button
                  key={book.id}
                  className="bookhero-dot"
                  onClick={() => setActiveIndex(index)}
                  style={{
                    width: index === activeIndex ? '22px' : '7px',
                    height: '7px',
                    borderRadius: '99px',
                    background:
                      index === activeIndex ? 'linear-gradient(to right, #ff6b2b, #ff9562)' : 'rgba(194,65,12,0.24)',
                    boxShadow: index === activeIndex ? '0 0 10px rgba(255,107,43,0.7)' : 'none',
                  }}
                />
              ))}

              <button
                className="bookhero-nav-btn"
                onClick={() => go(1)}
                style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'linear-gradient(135deg, #ff6b2b, #ff3d00)',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 16px rgba(255,107,43,0.45)',
                }}
              >
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default BooksHero;
